import { cn } from "@/lib/utils";

interface Props {
  data: Record<string, number>;
}

function getHeatFill(value: number): string {
  if (value <= 25) return "#22c55e";
  if (value <= 50) return "#eab308";
  if (value <= 75) return "#f97316";
  return "#ef4444";
}

function getHeatTextColor(value: number): string {
  if (value <= 25) return "text-green-700 dark:text-green-400";
  if (value <= 50) return "text-yellow-700 dark:text-yellow-400";
  if (value <= 75) return "text-orange-700 dark:text-orange-400";
  return "text-red-700 dark:text-red-400";
}

export function HeatMap({ data }: Props) {
  const frente = data["frente"] ?? 0;
  const traseira = data["traseira"] ?? 0;
  const latEsq = data["lateral_esquerda"] ?? 0;
  const latDir = data["lateral_direita"] ?? 0;
  const motor = data["motor"] ?? 0;
  const interior = data["interior"] ?? 0;

  const regions = [
    { id: "frente", label: "Frente", value: frente, path: "M 38,10 L 62,10 Q 68,10 70,16 L 72,28 L 28,28 L 30,16 Q 32,10 38,10 Z" },
    { id: "motor", label: "Motor", value: motor, path: "M 28,28 L 72,28 L 74,48 L 26,48 Z" },
    { id: "lat_esq", label: "Lat. Esq.", value: latEsq, path: "M 20,48 L 26,48 L 26,130 L 20,130 Q 16,130 16,126 L 16,52 Q 16,48 20,48 Z" },
    { id: "lat_dir", label: "Lat. Dir.", value: latDir, path: "M 74,48 L 80,48 Q 84,48 84,52 L 84,126 Q 84,130 80,130 L 74,130 L 74,48 Z" },
    { id: "interior", label: "Interior", value: interior, path: "M 26,48 L 74,48 L 74,130 L 26,130 Z" },
    { id: "traseira", label: "Traseira", value: traseira, path: "M 26,130 L 74,130 L 72,150 Q 70,158 62,158 L 38,158 Q 32,158 30,150 L 28,130 Z" },
  ];

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
        Mapa de Calor — Vista Superior
      </h3>
      <div className="flex flex-col md:flex-row items-center gap-6">
        {/* SVG Car */}
        <div className="relative w-[220px] h-[360px] shrink-0">
          <svg viewBox="10 4 80 162" className="w-full h-full drop-shadow-lg">
            {/* Car body outline */}
            <path
              d="M 38,10 L 62,10 Q 68,10 70,16 L 84,52 Q 84,48 80,48 L 74,48 L 74,130 L 80,130 Q 84,130 84,126 L 84,52 Q 84,48 80,48 L 72,28 L 74,48 L 84,52 L 84,126 Q 84,130 80,130 L 74,130 L 72,150 Q 70,158 62,158 L 38,158 Q 32,158 30,150 L 26,130 L 20,130 Q 16,130 16,126 L 16,52 Q 16,48 20,48 L 26,48 L 28,28 L 30,16 Q 32,10 38,10 Z"
              fill="none"
              stroke="currentColor"
              strokeWidth="0.8"
              className="text-border"
            />
            {/* Regions */}
            {regions.map((r) => (
              <g key={r.id}>
                <path
                  d={r.path}
                  fill={getHeatFill(r.value)}
                  fillOpacity={0.35}
                  stroke={getHeatFill(r.value)}
                  strokeWidth="1"
                  strokeOpacity={0.6}
                  className="transition-all duration-500"
                />
              </g>
            ))}
            {/* Wheels */}
            <rect x="12" y="38" width="6" height="16" rx="2" fill="currentColor" className="text-muted-foreground/40" />
            <rect x="82" y="38" width="6" height="16" rx="2" fill="currentColor" className="text-muted-foreground/40" />
            <rect x="12" y="118" width="6" height="16" rx="2" fill="currentColor" className="text-muted-foreground/40" />
            <rect x="82" y="118" width="6" height="16" rx="2" fill="currentColor" className="text-muted-foreground/40" />
            {/* Windshield lines */}
            <line x1="30" y1="48" x2="70" y2="48" stroke="currentColor" strokeWidth="0.5" className="text-muted-foreground/30" />
            <line x1="28" y1="130" x2="72" y2="130" stroke="currentColor" strokeWidth="0.5" className="text-muted-foreground/30" />
          </svg>
        </div>

        {/* Legend */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-2 gap-2 w-full">
          {regions.map((r) => (
            <div
              key={r.id}
              className="flex items-center gap-2 rounded-lg border p-2.5 transition-all"
              style={{ borderColor: getHeatFill(r.value) + "66", backgroundColor: getHeatFill(r.value) + "15" }}
            >
              <div
                className="w-3 h-3 rounded-sm shrink-0"
                style={{ backgroundColor: getHeatFill(r.value) }}
              />
              <div className="min-w-0">
                <p className="text-xs font-medium text-foreground truncate">{r.label}</p>
                <p className={cn("text-sm font-bold", getHeatTextColor(r.value))}>{r.value}%</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
